import React from "react";
import { View, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";

export type ProgressColorKey = "neutral" | "attention" | "pending" | "todo" | "indigo" | "complete";

export interface ProgressCellProps {
  startTime?: string | Date | null | undefined;
  endTime?: string | Date | null | undefined;
  /** Override calculated progress with explicit value (0-100) */
  progress?: number;
  /** Show percentage text inside the pill */
  showLabel?: boolean;
  /** Show "COMPLETE" text at 100%, or just "100%" (default: true) */
  showCompleteLabel?: boolean;
  /** Minimum width for the pill */
  minWidth?: number;
  /** Override color with a specific theme color key */
  colorKey?: ProgressColorKey;
}

/**
 * Calculate progress percentage based on time elapsed
 * Returns 0-100 value suitable for sorting
 */
export function calculateProgress(
  startTime: string | Date | null | undefined,
  endTime: string | Date | null | undefined,
): number {
  if (!startTime || !endTime) return 0;

  const now = Date.now();
  const start = new Date(startTime).getTime();
  const end = new Date(endTime).getTime();

  if (isNaN(start) || isNaN(end)) return 0;
  if (now < start) return 0; // Not started
  if (now >= end) return 100; // Completed

  return Math.round(((now - start) / (end - start)) * 100);
}

/**
 * Progress cell component - torrent-style progress bar
 * Displays a pill with fill proportional to progress
 */
export function ProgressCell({
  startTime,
  endTime,
  progress: explicitProgress,
  showLabel = true,
  showCompleteLabel = true,
  minWidth = 70,
  colorKey,
}: ProgressCellProps) {
  const { colors } = useTheme();

  // Use explicit progress if provided, otherwise calculate
  const progress = explicitProgress ?? calculateProgress(startTime, endTime);

  // Get color palette based on colorKey or progress-based default
  const getColorPalette = (): { border: string; background: string } => {
    // If explicit colorKey provided, use it
    if (colorKey) {
      const palette = colors[colorKey];
      if (palette && typeof palette === "object" && "border" in palette) {
        return palette as { border: string; background: string };
      }
    }
    // Default: 0% = gray (neutral), 1-99% = green (complete), 100% = blue (indigo)
    if (progress === 0) return colors.neutral;
    if (progress === 100) return colors.indigo;
    return colors.complete;
  };

  const palette = getColorPalette();
  const fillColor = palette.border;
  const backgroundColor = palette.background;

  // Label text: show "COMPLETE" for 100% (if enabled), percentage otherwise
  const getLabelText = () => {
    if (progress === 100) return showCompleteLabel ? "COMPLETE" : "100%";
    return `${progress}%`;
  };

  return (
    <View style={[styles.container, { backgroundColor, minWidth }]}>
      <View
        style={[
          styles.fill,
          {
            backgroundColor: fillColor,
            width: `${progress}%`,
          },
        ]}
      />
      {showLabel && (
        <View style={styles.labelContainer}>
          <ThemedText style={[styles.label, progress > 50 ? { color: "white" } : { color: colors.text }]}>
            {getLabelText()}
          </ThemedText>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    height: 20,
    borderRadius: 4,
    overflow: "hidden",
    position: "relative",
  },
  fill: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    borderRadius: 4,
  },
  labelContainer: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  label: {
    fontSize: 10,
    fontWeight: "600",
  },
});
